import type { components } from "./generated/schema";

export type Problem = components["schemas"]["Problem"];

/** Thrown for every non-2xx response; carries the RFC 7807 body (standards/error-handling.md). */
export class ApiProblemError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: Problem,
  ) {
    super(problem.detail ?? problem.title);
    this.name = "ApiProblemError";
  }
  get code(): string {
    return this.problem.code;
  }
}

export interface ApiClientOptions {
  /** e.g. "/api/v1" (customer) or "/console/api/v1" (console) is part of every path; baseUrl is the origin or "" */
  baseUrl?: string;
  /** The single bearer-injection point of the frontend. */
  getToken: () => Promise<string | undefined>;
  getLocale?: () => string | undefined;
  fetchImpl?: typeof fetch;
  onUnauthorized?: () => void;
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** a fresh token for this call only (step-up re-authentication, BR-175) */
  token?: string;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export function createApiClient(opts: ApiClientOptions) {
  const doFetch = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));

  async function request<T>(method: Method, path: string, req: RequestOptions = {}): Promise<T> {
    const url = new URL((opts.baseUrl ?? "") + path, opts.baseUrl ? undefined : "http://localhost");
    for (const [k, v] of Object.entries(req.query ?? {}))
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    const token = req.token ?? (await opts.getToken());
    const headers: Record<string, string> = { accept: "application/json", ...(req.headers ?? {}) };
    if (token) headers.authorization = `Bearer ${token}`;
    const locale = opts.getLocale?.();
    if (locale) headers["x-gp-locale"] = locale;
    if (req.idempotencyKey) headers["idempotency-key"] = req.idempotencyKey;
    if (req.body !== undefined) headers["content-type"] = "application/json";
    const target = opts.baseUrl ? url.toString() : `${url.pathname}${url.search}`;
    const res = await doFetch(target, {
      method,
      headers,
      ...(req.body !== undefined ? { body: JSON.stringify(req.body) } : {}),
      ...(req.signal ? { signal: req.signal } : {}),
    });
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : undefined;
    if (!res.ok) {
      if (res.status === 401) opts.onUnauthorized?.();
      const problem = (json ?? {
        type: "about:blank",
        title: res.statusText,
        status: res.status,
        code: "internal_error",
      }) as Problem;
      throw new ApiProblemError(res.status, problem);
    }
    return json as T;
  }

  return {
    request,
    get: <T>(path: string, req?: RequestOptions) => request<T>("GET", path, req),
    post: <T>(path: string, body?: unknown, req?: RequestOptions) =>
      request<T>("POST", path, { ...req, body }),
    put: <T>(path: string, body?: unknown, req?: RequestOptions) => request<T>("PUT", path, { ...req, body }),
    patch: <T>(path: string, body?: unknown, req?: RequestOptions) =>
      request<T>("PATCH", path, { ...req, body }),
    delete: <T>(path: string, req?: RequestOptions) => request<T>("DELETE", path, req),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
